/** The assistant's API: status (does the Ask button show), ask, explain, and the helpful count. */
import { useQuery } from '@tanstack/react-query';
import type { AssistantAnswer, AssistantExplainRequest, AssistantFeedbackRequest, AssistantLang, AssistantMode, AssistantStatus, AssistantThread, CarefulTranslation, CarefulTranslationRequest, AssistantUsageReport } from '@shared/assistant';
import { apiFetch } from './api';

/** Everyone but the crew may ask (the server says the same). */
export const ASSISTANT_ROLES = ['admin', 'coadmin', 'scheduler', 'viewer'] as const;

export function useAssistantStatus(enabled: boolean) {
  return useQuery({
    queryKey: ['assistant-status'],
    queryFn: () => apiFetch<AssistantStatus>('/api/assistant/status'),
    enabled,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });
}

const post = <T,>(path: string, body: unknown) => apiFetch<T>(path, { method: 'POST', body: JSON.stringify(body) });

/** 1.8.0: a typed question, or a press — "What can I do here?" (here), "Walk me through…" (walk), a chip (pick + thread). */
export interface AskExtra {
  mode?: AssistantMode;
  thread?: AssistantThread;
  pick?: string;
}

/**
 * 1.8.4 (D-1.8.4-1): the page a question is asked from. `/` holds two tabs — Today (the whole house) and Housekeeping's
 * day — so when Today is the one showing the assistant is told `/house-today` (shared/app-screens.ts `house`).
 */
export function askPath(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  const here = window.location.pathname;
  return here === '/' && document.querySelector('[data-testid="house-today"]') ? '/house-today' : here;
}

export const askAssistant = (question: string, lang: AssistantLang, extra: AskExtra = {}) => post<AssistantAnswer>('/api/assistant', { question, lang, path: askPath(), ...extra });

export const explainWithAssistant = (req: AssistantExplainRequest) => post<AssistantAnswer>('/api/manage/assistant/explain', req);

export const sendAssistantFeedback = (helpful: boolean, about: Omit<AssistantFeedbackRequest, 'helpful'> = {}) => post<{ ok: true }>('/api/assistant/feedback', { helpful, ...about });

/** 1.8.0 — Health: the assistant's calls and cost, day by day (administrators). */
export function useAssistantUsage(enabled: boolean) {
  return useQuery({ queryKey: ['assistant-usage'], queryFn: () => apiFetch<AssistantUsageReport>('/api/manage/assistant/usage'), enabled, staleTime: 60_000 });
}

/** "Careful translation" on the review card: names the field; the server reads the crew's words itself. */
export const carefulTranslation = (completionId: string, req: CarefulTranslationRequest) =>
  post<CarefulTranslation>(`/api/completions/${encodeURIComponent(completionId)}/careful-translation`, req);

/** Opens the Ask panel from anywhere (Help & Support's "Ask the assistant"); the floating button listens for it. */
export const OPEN_ASK_EVENT = 'abf:open-ask';
